import { useEffect, useMemo, useState } from "react";
import { List, Columns3, Search, Download, X, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ColumnPicker } from "@/components/crm/ViewControls";
import Pager from "@/components/crm/Pager";
import RecordsTable from "./RecordsTable";
import RecordsBoard from "./RecordsBoard";
import RecordDrawer from "./RecordDrawer";
import { buildParams, toCsv, optVal, optLabel, SELECT_TYPES } from "@/lib/records";

const ls = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };

function FilterBar({ selects, filters, setFilters }) {
  const [pick, setPick] = useState("");
  const shown = selects.filter((f) => f.key in filters);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {shown.map((f) => (
        <label key={f.key} className="flex items-center gap-1 text-xs rounded border border-[var(--border-strong)] pl-2" data-testid={`records-filter-${f.key}`}>
          {f.label}
          <select className="bg-transparent py-1 text-xs" value={filters[f.key]} onChange={(e) => setFilters({ ...filters, [f.key]: e.target.value })} data-testid={`records-filter-value-${f.key}`}>
            <option value="">Any</option>
            {(f.options || []).map((o) => <option key={String(optVal(o))} value={optVal(o)}>{optLabel(o)}</option>)}
          </select>
          <button className="px-1.5 hover:text-[var(--breach)]" onClick={() => { const n = { ...filters }; delete n[f.key]; setFilters(n); }} data-testid={`records-filter-remove-${f.key}`}><X size={12} /></button>
        </label>
      ))}
      {selects.some((f) => !(f.key in filters)) && (
        <select className="text-xs px-2 py-1 rounded border" value={pick} onChange={(e) => { setFilters({ ...filters, [e.target.value]: "" }); setPick(""); }} data-testid="records-add-filter">
          <option value="" disabled>+ Filter by…</option>
          {selects.filter((f) => !(f.key in filters)).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
      )}
    </div>
  );
}

export default function RecordsView({ mod, total0 }) {
  const fields = useMemo(() => (mod.fields || []).filter((f) => f.key), [mod.fields]);
  const selects0 = fields.filter((f) => SELECT_TYPES.includes(f.type) || (f.options || []).length || f.key === "row_type" || f.key === mod.board_field);
  const boardField = mod.board_field && mod.board_field !== "stage" ? mod.board_field : selects0.find((f) => f.key === "row_type")?.key || selects0[0]?.key;
  const defSort = mod.default_sort || (fields.some((f) => f.key === "updated_date") ? "updated_date" : "updated_at");
  const pk = `records_${mod._id}`;
  const [view, setView] = useState(ls(`${pk}_view`, "list"));
  const [columns, setColumns] = useState(ls(`${pk}_cols`, fields.slice(0, 7).map((f) => f.key)));
  const [qIn, setQIn] = useState("");
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState(ls(`${pk}_filters`, {}));
  const [sort, setSort] = useState({ k: defSort, dir: mod.default_dir || "desc" });
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [data, setData] = useState({ rows: [], total: total0 });
  const [groups, setGroups] = useState([]);
  const selects = selects0.map((f) => ((f.options || []).length || f.key !== boardField ? f : { ...f, options: groups.map((g) => g.value).filter((v) => v != null && v !== "") }));
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { localStorage.setItem(`${pk}_view`, JSON.stringify(view)); localStorage.setItem(`${pk}_cols`, JSON.stringify(columns)); localStorage.setItem(`${pk}_filters`, JSON.stringify(filters)); }, [pk, view, columns, filters]);
  useEffect(() => { const t = setTimeout(() => { setQ(qIn.trim()); setPage(0); }, 300); return () => clearTimeout(t); }, [qIn]);
  const base = useMemo(() => ({ q, filters, sort: sort.k, dir: sort.dir }), [q, filters, sort]);
  useEffect(() => {
    const p = buildParams({ ...base, limit: 1, skip: 0 });
    if (boardField) p.set("group", boardField);
    api.get(`/crm/modules/${mod._id}/record-stats?${p}`).then((r) => setGroups(r.data.groups));
  }, [base, boardField, mod._id, tick]);
  useEffect(() => {
    if (view !== "list") return;
    setLoading(true);
    api.get(`/crm/modules/${mod._id}/records?${buildParams({ ...base, limit: size, skip: page * size })}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [base, page, size, view, mod._id, tick]);
  const total = groups.reduce((a, g) => a + g.count, 0) || (view === "list" ? data.total : 0);
  const onSort = (k) => { setSort((s) => ({ k, dir: s.k === k && s.dir === "desc" ? "asc" : "desc" })); setPage(0); };
  const exportCsv = async () => {
    const r = await api.get(`/crm/modules/${mod._id}/records?${buildParams({ ...base, limit: 5000, skip: 0 })}`);
    const blob = new Blob([toCsv(r.data.rows, fields)], { type: "text/csv" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `${mod._id}-${new Date().toISOString().slice(0, 10)}.csv` });
    a.click();
    toast.success(`Exported ${r.data.rows.length} records`);
  };
  return (
    <div className="p-4 sm:p-6 space-y-3" data-testid="records-view">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-medium">{mod.name}</h1>
        <span className="text-sm text-[var(--text-dim)]" data-testid="records-total">{total.toLocaleString()} record{total === 1 ? "" : "s"}</span>
        <div className="ml-auto flex items-center gap-1">
          <button className="btn btn-primary text-xs mr-2" onClick={() => setOpen({ data: {} })} data-testid="records-new"><Plus size={13} />New record</button>
          {[["list", "List", List], ["board", "Board", Columns3]].filter(([k]) => (mod.views || ["list", "board"]).includes(k) || k === "list").map(([k, l, I]) => (
            <button key={k} className={`btn text-xs ${view === k ? "bg-[var(--hover)] text-[var(--text)]" : ""}`} onClick={() => setView(k)} data-testid={`records-view-${k}`}><I size={13} />{l}</button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-[var(--border-strong)] w-full sm:w-64">
          <Search size={13} className="text-[var(--text-faint)]" />
          <input className="flex-1 bg-transparent text-sm outline-none" placeholder="Search records" value={qIn} onChange={(e) => setQIn(e.target.value)} data-testid="records-search" />
        </div>
        <FilterBar selects={selects} filters={filters} setFilters={(f) => { setFilters(f); setPage(0); }} />
        <div className="ml-auto flex items-center gap-2">
          {view === "list" && <ColumnPicker columns={columns} onChange={setColumns} fields={[...fields.map((f) => ({ key: f.key, label: f.label || f.key })), { key: "updated_at", label: "Updated" }, { key: "created_at", label: "Created" }]} />}
          <button className="btn text-xs" onClick={exportCsv} data-testid="records-export"><Download size={13} />CSV</button>
        </div>
      </div>
      {view === "list" ? (
        <div>
          <RecordsTable rows={data.rows} columns={columns} fields={fields} sort={sort.k} dir={sort.dir} onSort={onSort} onOpen={setOpen} loading={loading} />
          <Pager page={page} size={size} total={data.total || 0} onPage={setPage} onSize={(n) => { setSize(n); setPage(0); }} />
        </div>
      ) : <RecordsBoard mkey={mod._id} field={boardField} fields={fields} groups={groups} base={base} onOpen={setOpen} titleField={mod.title_field} cardFields={mod.card_fields} reload={tick} />}
      <RecordDrawer mod={mod} record={open} fields={fields} onClose={() => setOpen(null)} onSaved={(r) => { setTick((t) => t + 1); if (r) setOpen(r); }} />
    </div>
  );
}
